import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  SignOut, 
  Flask,
  Gear,
  CaretDown,
  CaretRight,
  CaretLeft,
  MagnifyingGlass,
  Video,
  Clock,
  Path,
  Gauge,
  Circle,
  ArrowClockwise,
  X,
  MapPin,
  Car,
  Bell,
  ChartLine,
  Users,
  Folder,
  FolderOpen,
  Truck,
  Warning,
  Lightning,
  Pause,
  Stop,
  Play,
  List,
  MapTrifold,
  Broadcast,
  Eye,
  GearSix,
  Question,
  Export,
  Printer,
  Plus,
  Minus,
  Crosshair,
  Polygon,
  Flag,
  CloudArrowUp,
} from '@phosphor-icons/react';
import { useAuthStore } from '@/store';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Custom vehicle icon - Tracksolid style
const createVehicleIcon = (status, heading = 0) => {
  const colors = {
    moving: '#22c55e',    // Green - moving
    stopped: '#ef4444',   // Red - stopped
    idle: '#eab308',      // Yellow - idle
    offline: '#9ca3af',   // Gray - offline
  };
  const color = colors[status] || colors.offline;
  
  return L.divIcon({
    className: 'vehicle-marker',
    html: `<div style="
      width: 32px; height: 32px; 
      position: relative;
      transform: rotate(${heading}deg);
    ">
      <svg viewBox="0 0 32 32" fill="${color}" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 2 L26 28 L16 22 L6 28 Z" stroke="#fff" stroke-width="1.5"/>
      </svg>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
};

// Map bounds updater
const MapBoundsUpdater = ({ devices, selectedDevice }) => {
  const map = useMap();
  
  useEffect(() => {
    if (selectedDevice) {
      const lat = parseFloat(selectedDevice.lat || 0);
      const lng = parseFloat(selectedDevice.lng || 0);
      if (lat !== 0 && lng !== 0) {
        map.flyTo([lat, lng], 16, { duration: 0.5 });
      }
    } else if (devices.length > 0) {
      const validDevices = devices.filter(d => {
        const lat = parseFloat(d.lat || 0);
        const lng = parseFloat(d.lng || 0);
        return lat !== 0 && lng !== 0;
      });
      if (validDevices.length > 0) {
        const bounds = L.latLngBounds(validDevices.map(d => [parseFloat(d.lat), parseFloat(d.lng)]));
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
      }
    }
  }, [devices, selectedDevice, map]);
  
  return null;
};

const Classic = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const mapRef = useRef(null);
  
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [devices, setDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(['default']);
  const [mapType, setMapType] = useState('hybrid'); // street, satellite, hybrid
  
  const token = localStorage.getItem('4sat_token');
  
  // Fetch devices
  useEffect(() => {
    if (!token) return;
    
    const loadDevices = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/devices`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDevices(response.data || []);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching devices:', err);
        setLoading(false);
      }
    };
    
    loadDevices();
  }, [token]);
  
  // Fetch locations
  useEffect(() => {
    if (loading || devices.length === 0 || !token) return;
    
    const loadLocations = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/devices/locations/all`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const locations = response.data || [];
        
        setDevices(prev => prev.map(device => {
          const loc = locations.find(l => l.imei === device.imei);
          if (loc) {
            return { ...device, ...loc };
          }
          return device;
        }));
      } catch (err) {
        console.error('Error fetching locations:', err);
      }
    };
    
    loadLocations();
    const interval = setInterval(loadLocations, 30000);
    return () => clearInterval(interval);
  }, [loading, devices.length, token]);
  
  // Filter devices
  useEffect(() => {
    let filtered = devices;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(d => 
        d.deviceName?.toLowerCase().includes(query) ||
        d.imei?.includes(query)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(d => getDeviceStatus(d) === statusFilter);
    }
    
    setFilteredDevices(filtered);
  }, [devices, searchQuery, statusFilter]);
  
  // Helper para verificar si tiene coordenadas válidas
  const hasValidCoordinates = (device) => {
    const lat = parseFloat(device?.lat || 0);
    const lng = parseFloat(device?.lng || 0);
    // Coordenadas válidas: no son exactamente 0,0 y están en rango
    return lat !== 0 && lng !== 0 && 
           lat >= -90 && lat <= 90 && 
           lng >= -180 && lng <= 180;
  };
  
  const getDeviceStatus = (device) => {
    const isOnline = device.status === '1' || device.status === 1;
    if (!isOnline) return 'offline';
    const speed = parseFloat(device.speed || 0);
    if (speed > 3) return 'moving';
    if (speed > 0) return 'idle';
    return 'stopped';
  };
  
  const getStatusColor = (status) => {
    const colors = {
      moving: 'bg-green-500',
      stopped: 'bg-red-500',
      idle: 'bg-yellow-500',
      offline: 'bg-gray-400',
    };
    return colors[status] || colors.offline;
  };
  
  const getStatusText = (status) => {
    const texts = {
      moving: 'En movimiento',
      stopped: 'Detenido',
      idle: 'Ralentí',
      offline: 'Sin conexión',
    };
    return texts[status] || 'Desconocido';
  };
  
  // Stats
  const stats = {
    total: devices.length,
    moving: devices.filter(d => getDeviceStatus(d) === 'moving').length,
    stopped: devices.filter(d => getDeviceStatus(d) === 'stopped').length,
    idle: devices.filter(d => getDeviceStatus(d) === 'idle').length,
    offline: devices.filter(d => getDeviceStatus(d) === 'offline').length,
  };
  
  const handleLogout = () => {
    localStorage.removeItem('4sat_token');
    localStorage.removeItem('4sat_user');
    logout();
    navigate('/login');
  };
  
  const refreshData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/devices/locations/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const locations = response.data || [];
      setDevices(prev => prev.map(device => {
        const loc = locations.find(l => l.imei === device.imei);
        return loc ? { ...device, ...loc } : device;
      }));
    } catch (err) {
      console.error('Error:', err);
    }
  };

  // Map tile layers
  const getTileLayer = () => {
    switch (mapType) {
      case 'satellite':
        return 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
      case 'hybrid':
        return 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
      default:
        return 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-100 text-gray-800 overflow-hidden">
      {/* Top Header - Tracksolid style */}
      <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 shadow-sm z-50">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <img 
            src="/assets/4sat-logo.png" 
            alt="4SAT" 
            className="h-10 object-contain"
          />
          <div className="hidden lg:block border-l border-gray-200 pl-3">
            <span className="text-xs text-gray-500 font-medium">
              Fatiga, Somnolencia y Distracción
            </span>
          </div>
        </div>
        
        {/* Center - Main Navigation */}
        <nav className="hidden md:flex items-center gap-1">
          {[
            { icon: MapTrifold, label: 'Monitor', active: true },
            { icon: Path, label: 'Recorridos' },
            { icon: ChartLine, label: 'Reportes' },
            { icon: Video, label: 'Video', onClick: () => navigate('/video') },
            { icon: Bell, label: 'Alertas' },
            { icon: Polygon, label: 'Geocercas' },
          ].map((item, i) => (
            <button
              key={i}
              onClick={item.onClick}
              className={`h-10 px-4 rounded flex items-center gap-2 text-sm font-medium transition-colors ${
                item.active 
                  ? 'bg-blue-50 text-blue-600' 
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              <item.icon size={18} />
              {item.label}
            </button>
          ))}
        </nav>
        
        {/* Right - User Menu */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="h-8 px-3 rounded bg-emerald-50 text-emerald-600 text-xs font-medium hover:bg-emerald-100 transition-colors flex items-center gap-1.5 border border-emerald-200"
          >
            <Flask size={14} />
            <span className="hidden sm:inline">Beta</span>
          </button>
          
          <button className="p-2 rounded hover:bg-gray-100 text-gray-500">
            <Question size={20} />
          </button>
          
          <button className="p-2 rounded hover:bg-gray-100 text-gray-500">
            <GearSix size={20} />
          </button>
          
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 h-9 px-3 rounded hover:bg-gray-100 transition-colors"
            >
              <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-gray-800 text-xs font-medium">
                {(user?.account || 'U')[0].toUpperCase()}
              </div>
              <span className="hidden md:block text-sm text-gray-800 max-w-[120px] truncate">
                {user?.account || 'Usuario'}
              </span>
              <CaretDown size={12} className="text-gray-500" />
            </button>
            
            {showUserMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="text-sm font-medium text-gray-800">{user?.account}</div>
                    <div className="text-xs text-gray-500">Administrador</div>
                  </div>
                  <button
                    onClick={() => navigate('/users')}
                    className="w-full px-4 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Users size={16} />
                    Gestión de Usuarios
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="w-full px-4 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Flask size={16} className="text-emerald-500" />
                    Versión Beta
                  </button>
                  <button
                    onClick={() => navigate('/bridge')}
                    className="w-full px-4 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <CloudArrowUp size={16} className="text-blue-500" />
                    Configuración Bridge
                  </button>
                  <hr className="border-gray-100" />
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <SignOut size={16} />
                    Cerrar Sesión
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Device Tree */}
        <aside className={`${sidebarCollapsed ? 'w-12' : 'w-72'} bg-white border-r border-gray-200 flex flex-col transition-all duration-200 shrink-0`}>
          {/* Sidebar Header */}
          {!sidebarCollapsed && (
            <div className="p-3 border-b border-gray-200">
              {/* Search */}
              <div className="relative mb-3">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar dispositivo..."
                  className="w-full h-9 pl-9 pr-8 rounded-md bg-gray-50 border border-gray-200 text-sm text-gray-800 placeholder:text-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-500"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              
              {/* Status Filter Pills */}
              <div className="flex flex-wrap gap-1">
                {[
                  { key: 'all', label: 'Todos', count: stats.total, color: 'bg-gray-500' },
                  { key: 'moving', label: 'Mov', count: stats.moving, color: 'bg-green-500' },
                  { key: 'stopped', label: 'Det', count: stats.stopped, color: 'bg-red-500' },
                  { key: 'idle', label: 'Ral', count: stats.idle, color: 'bg-yellow-500' },
                  { key: 'offline', label: 'Off', count: stats.offline, color: 'bg-gray-400' },
                ].map(f => (
                  <button
                    key={f.key}
                    onClick={() => setStatusFilter(f.key)}
                    className={`h-7 px-2 rounded text-xs font-medium transition-colors flex items-center gap-1 ${
                      statusFilter === f.key 
                        ? 'bg-blue-500 text-gray-800' 
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${f.color}`} />
                    {f.count}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Device List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <ArrowClockwise size={24} className="animate-spin text-blue-500" />
              </div>
            ) : sidebarCollapsed ? (
              <div className="py-2 flex flex-col items-center gap-1">
                <button
                  onClick={() => setSidebarCollapsed(false)}
                  className="p-2 rounded hover:bg-gray-100"
                >
                  <CaretRight size={16} />
                </button>
              </div>
            ) : (
              <div className="py-1">
                {/* Group header */}
                <button
                  onClick={() => setExpandedGroups(prev => 
                    prev.includes('default') 
                      ? prev.filter(g => g !== 'default')
                      : [...prev, 'default']
                  )}
                  className="w-full px-3 py-2 flex items-center gap-2 hover:bg-gray-100 text-left"
                >
                  {expandedGroups.includes('default') ? (
                    <FolderOpen size={18} className="text-blue-500" />
                  ) : (
                    <Folder size={18} className="text-gray-500" />
                  )}
                  <span className="text-sm font-medium text-gray-800 flex-1">
                    Mis Vehículos
                  </span>
                  <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                    {filteredDevices.length}
                  </span>
                  {expandedGroups.includes('default') ? (
                    <CaretDown size={14} className="text-gray-500" />
                  ) : (
                    <CaretRight size={14} className="text-gray-500" />
                  )}
                </button>
                
                {/* Device items */}
                {expandedGroups.includes('default') && filteredDevices.map(device => {
                  const status = getDeviceStatus(device);
                  const isSelected = selectedDevice?.imei === device.imei;
                  const hasLocation = hasValidCoordinates(device);
                  
                  return (
                    <button
                      key={device.imei}
                      onClick={() => setSelectedDevice(isSelected ? null : device)}
                      className={`w-full px-3 py-2 flex items-center gap-2 text-left transition-colors ${
                        isSelected 
                          ? 'bg-blue-50 border-l-3 border-blue-500' 
                          : 'hover:bg-gray-100 border-l-3 border-transparent'
                      }`}
                    >
                      <div className={`w-2.5 h-2.5 rounded-full ${getStatusColor(status)}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-800 truncate">
                          {device.deviceName || device.imei}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          {hasLocation ? (
                            <>
                              <span>{parseFloat(device.speed || 0).toFixed(0)} km/h</span>
                              <span>•</span>
                              <span>{getStatusText(status)}</span>
                            </>
                          ) : (
                            <span className="text-orange-500 flex items-center gap-1">
                              <Warning size={12} />
                              Sin ubicación
                            </span>
                          )}
                        </div>
                      </div>
                      <CaretRight size={14} className="text-gray-600" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Collapse button */}
          {!sidebarCollapsed && (
            <div className="p-2 border-t border-gray-200">
              <button
                onClick={() => setSidebarCollapsed(true)}
                className="w-full h-8 rounded flex items-center justify-center gap-2 text-xs text-gray-500 hover:bg-gray-100"
              >
                <CaretLeft size={14} />
                Colapsar
              </button>
            </div>
          )}
        </aside>
        
        {/* Map Area */}
        <main className="flex-1 relative">
          {/* Map Type Selector */}
          <div className="absolute top-3 left-3 z-[1000] bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
            {[
              { key: 'street', label: 'Mapa' },
              { key: 'satellite', label: 'Satélite' },
              { key: 'hybrid', label: 'Híbrido' },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setMapType(t.key)}
                className={`px-3 py-2 text-xs font-medium transition-colors ${
                  mapType === t.key 
                    ? 'bg-blue-500 text-gray-800' 
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          
          {/* Map Tools */}
          <div className="absolute top-3 right-3 z-[1000] flex flex-col gap-2">
            <button
              onClick={refreshData}
              className="w-10 h-10 rounded-lg bg-white text-gray-500 hover:bg-gray-100 transition-colors flex items-center justify-center shadow-md border border-gray-200"
              title="Actualizar"
            >
              <ArrowClockwise size={18} />
            </button>
            <button
              className="w-10 h-10 rounded-lg bg-white text-gray-500 hover:bg-gray-100 transition-colors flex items-center justify-center shadow-md border border-gray-200"
              title="Centrar"
            >
              <Crosshair size={18} />
            </button>
          </div>
          
          <MapContainer
            center={[-33.4489, -70.6693]}
            zoom={5}
            className="w-full h-full"
            ref={mapRef}
            zoomControl={false}
          >
            <TileLayer
              url={getTileLayer()}
              attribution='&copy; Esri, DigitalGlobe'
            />
            {/* Labels overlay for hybrid */}
            {mapType === 'hybrid' && (
              <TileLayer
                url="https://stamen-tiles-{s}.a.ssl.fastly.net/toner-labels/{z}/{x}/{y}{r}.png"
                attribution='&copy; Stamen Design'
              />
            )}
            
            <ZoomControl position="bottomright" />
            <MapBoundsUpdater devices={filteredDevices.filter(d => hasValidCoordinates(d))} selectedDevice={selectedDevice} />
            
            {filteredDevices.filter(d => hasValidCoordinates(d)).map(device => (
              <Marker
                key={device.imei}
                position={[parseFloat(device.lat), parseFloat(device.lng)]}
                icon={createVehicleIcon(getDeviceStatus(device), parseFloat(device.direction || 0))}
                eventHandlers={{
                  click: () => setSelectedDevice(device),
                }}
              >
                <Popup className="tracksolid-popup" maxWidth={300}>
                  <div className="min-w-[250px]">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
                      <div className={`w-3 h-3 rounded-full ${getStatusColor(getDeviceStatus(device))}`} />
                      <span className="font-semibold text-gray-800">{device.deviceName || device.imei}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <div className="text-gray-500 text-xs">Estado</div>
                        <div className="font-medium">{getStatusText(getDeviceStatus(device))}</div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs">Velocidad</div>
                        <div className="font-medium">{parseFloat(device.speed || 0).toFixed(0)} km/h</div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs">Dirección</div>
                        <div className="font-medium">{device.direction || 0}°</div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs">GPS</div>
                        <div className="font-medium">{device.posType || 'GPS'}</div>
                      </div>
                      {device.gpsTime && (
                        <div className="col-span-2">
                          <div className="text-gray-500 text-xs">Última actualización</div>
                          <div className="font-medium text-xs">{new Date(device.gpsTime).toLocaleString('es-CL')}</div>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 mt-3 pt-3 border-t border-gray-200">
                      <button 
                        onClick={() => navigate(`/video?imei=${device.imei}`)}
                        className="flex-1 h-8 rounded bg-blue-500 text-gray-800 text-xs font-medium hover:bg-blue-600 flex items-center justify-center gap-1"
                      >
                        <Video size={14} />
                        Video
                      </button>
                      <button className="flex-1 h-8 rounded bg-gray-100 text-gray-800 text-xs font-medium hover:bg-gray-200 flex items-center justify-center gap-1">
                        <Path size={14} />
                        Recorrido
                      </button>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
          
          {/* Selected Device Info Panel */}
          {selectedDevice && (
            <div className="absolute bottom-4 left-4 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-[1000] overflow-hidden">
              <div className="p-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${getStatusColor(getDeviceStatus(selectedDevice))}`} />
                  <span className="font-semibold text-gray-800">{selectedDevice.deviceName || selectedDevice.imei}</span>
                </div>
                <button
                  onClick={() => setSelectedDevice(null)}
                  className="p-1 rounded hover:bg-gray-200 text-gray-500"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="p-3">
                {hasValidCoordinates(selectedDevice) ? (
                  <>
                    <div className="grid grid-cols-3 gap-3 text-center mb-3">
                      <div className="p-2 bg-gray-50 rounded">
                        <div className="text-xl font-bold text-blue-600">
                          {parseFloat(selectedDevice.speed || 0).toFixed(0)}
                        </div>
                        <div className="text-xs text-gray-500">km/h</div>
                      </div>
                      <div className="p-2 bg-gray-50 rounded">
                        <div className="text-xl font-bold text-gray-800">
                          {selectedDevice.direction || 0}°
                        </div>
                        <div className="text-xs text-gray-500">Dirección</div>
                      </div>
                      <div className="p-2 bg-gray-50 rounded">
                        <div className={`text-sm font-bold ${
                          getDeviceStatus(selectedDevice) === 'moving' ? 'text-green-600' :
                          getDeviceStatus(selectedDevice) === 'stopped' ? 'text-red-600' :
                          getDeviceStatus(selectedDevice) === 'idle' ? 'text-yellow-600' : 'text-gray-500'
                        }`}>
                          {getStatusText(getDeviceStatus(selectedDevice))}
                        </div>
                        <div className="text-xs text-gray-500">Estado</div>
                      </div>
                    </div>
                    {selectedDevice.gpsTime && (
                      <div className="text-xs text-gray-500 mb-3 flex items-center gap-1">
                        <Clock size={12} />
                        {new Date(selectedDevice.gpsTime).toLocaleString('es-CL')}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button 
                        onClick={() => navigate(`/video?imei=${selectedDevice.imei}`)}
                        className="flex-1 h-9 rounded bg-blue-500 text-gray-800 text-sm font-medium hover:bg-blue-600 flex items-center justify-center gap-1"
                      >
                        <Video size={16} />
                        Video en vivo
                      </button>
                      <button className="flex-1 h-9 rounded bg-gray-100 text-gray-800 text-sm font-medium hover:bg-gray-200 flex items-center justify-center gap-1">
                        <Path size={16} />
                        Recorrido
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <Warning size={32} className="mx-auto text-orange-400 mb-2" />
                    <p className="text-sm text-gray-500 mb-1">Sin ubicación disponible</p>
                    <p className="text-xs text-gray-500">
                      El dispositivo está {getStatusText(getDeviceStatus(selectedDevice)).toLowerCase()} o no ha reportado posición GPS aún.
                    </p>
                    <button 
                      onClick={() => navigate(`/video?imei=${selectedDevice.imei}`)}
                      className="mt-3 h-9 px-4 rounded bg-blue-500 text-gray-800 text-sm font-medium hover:bg-blue-600 flex items-center justify-center gap-1 mx-auto"
                    >
                      <Video size={16} />
                      Ver Video
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Status Legend */}
          <div className="absolute bottom-4 right-4 z-[1000] bg-white rounded-lg shadow-md border border-gray-200 p-3">
            <div className="text-xs font-medium text-gray-800 mb-2">Estado</div>
            <div className="space-y-1.5">
              {[
                { color: 'bg-green-500', label: 'En movimiento', count: stats.moving },
                { color: 'bg-red-500', label: 'Detenido', count: stats.stopped },
                { color: 'bg-yellow-500', label: 'Ralentí', count: stats.idle },
                { color: 'bg-gray-400', label: 'Sin conexión', count: stats.offline },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <div className={`w-2.5 h-2.5 rounded-full ${item.color}`} />
                  <span className="text-gray-500 flex-1">{item.label}</span>
                  <span className="text-gray-500 font-medium">{item.count}</span>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Classic;
